import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, CheckCircle, Clock, LogOut, UserCog, ArrowRightLeft, Menu, X } from 'lucide-react';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import { apiClient } from '../../services/apiClient';
import { addNotification } from '../../store/slices/uiSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout, setUser } from '../../store/slices/authSlice';
import { store } from '../../store/store';
import { logError } from '../../utils/errorLogger';
import { authAPI } from '../../services/api';
import { useRoleSwitching } from '../../hooks/useRoleSwitching';
import ConfirmationModal from '../../components/UI/ConfirmationModal';

interface Event {
  id: string;
  title: string;
  description: string;
  event_date: string;
  event_time: string;
  location: string;
  host_name: string;
  status: string;
  guest_count: number;
  attended_count: number;
}

interface UsherDashboardPageProps {}

const UsherDashboardPage: React.FC<UsherDashboardPageProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  
  // Role switching hook
  const {
    switchingRole,
    showRoleSwitchConfirm,
    targetRole,
    availableRoles,
    setShowRoleSwitchConfirm,
    setTargetRole,
    handleSwitchRoleClick,
    handleSwitchRole,
  } = useRoleSwitching('usher');
  
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuPosition, setMobileMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Calculate mobile menu position
  const calculateMobileMenuPosition = () => {
    if (!mobileMenuButtonRef.current) return;

    const rect = mobileMenuButtonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Position dropdown below the button, aligned to the right
    const top = rect.bottom + 4;
    const width = Math.min(300, viewportWidth - 16); // Max 300px or viewport width minus padding
    const left = Math.max(8, viewportWidth - width - 8); // Align to right with 8px padding
    
    setMobileMenuPosition({ top, left, width });
  };

  // Update position when menu opens
  useEffect(() => {
    if (isMobileMenuOpen) {
      calculateMobileMenuPosition();
      
      // Recalculate on scroll and resize
      const handleUpdate = () => {
        if (isMobileMenuOpen) {
          calculateMobileMenuPosition();
        }
      };
      
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    } else {
      setMobileMenuPosition(null);
    }
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuButtonRef.current &&
        !mobileMenuButtonRef.current.contains(event.target as Node) &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
        setMobileMenuPosition(null);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Refresh user profile on dashboard load to get latest roles
  // This ensures we have the most up-to-date roles (e.g., if admin added roles after login)
  useEffect(() => {
    const refreshUserProfile = async () => {
      try {
        const profileResponse = await authAPI.getProfile();
        if (profileResponse.data.success && profileResponse.data.data) {
          const updatedUser = {
            ...profileResponse.data.data,
            roles: profileResponse.data.data.roles || [profileResponse.data.data.role],
            active_role: profileResponse.data.data.active_role || profileResponse.data.data.role,
          };
          store.dispatch(setUser(updatedUser));
        }
      } catch (err) {
        logError('Failed to refresh user profile', err);
      }
    };
    
    // Always refresh profile on dashboard load to get latest roles
    refreshUserProfile();
  }, []); // Only run once on mount

  const fetchUsherEvents = useCallback(async () => {
    try {
      setLoading(true);
      
      // Disable caching to always get fresh data (important for expiry checks)
      const response = await apiClient.get('/ushers/events/', { cache: false });
      
      if (response.data.success) {
        setEvents(response.data.data);
      }
    } catch (error: any) {
      // Error fetching usher events
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load events. Please try again.',
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    // Fetch usher events
    fetchUsherEvents();
  }, [navigate, isAuthenticated, user, fetchUsherEvents]);

  const handleLogout = async () => {
    try {
      // Dispatch logout action which handles API call, clearing Redux state, and localStorage
      await dispatch(logout(false)).unwrap();
    } catch (error: any) {
      logError('Logout failed', error);
      // Continue with navigation even if API fails (logout action clears local state anyway)
    }
    
    // Navigate to login page
    navigate('/login');
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/usher/events/${eventId}/guests`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rescheduled':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'canceled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
          <p className="text-sm sm:text-base text-gray-400">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 relative">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 sm:gap-4 py-3 sm:py-0 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="/logo.jpg" 
                alt="Mirsal Logo" 
                className="h-6 w-6 sm:h-8 sm:w-8 rounded-lg object-cover flex-shrink-0"
              />
              <span className="text-base sm:text-lg md:text-xl font-bold truncate">MIRSAL - Usher Dashboard</span>
            </div>
            
            {/* Desktop Menu - Hidden on mobile/tablet */}
            <div className="hidden lg:flex items-center gap-3 xl:gap-4">
              {/* Role Switching Buttons */}
              {availableRoles.length > 0 && (
                <div className="flex items-center gap-2">
                  {availableRoles.map((availableRole) => {
                    const colorClasses = {
                      admin: 'bg-red-500/20 hover:bg-red-500/30 border-red-500/30',
                      host: 'bg-purple-500/20 hover:bg-purple-500/30 border-purple-500/30',
                      guest: 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/30',
                      usher: 'bg-green-500/20 hover:bg-green-500/30 border-green-500/30',
                    };
                    return (
                      <button
                        key={availableRole.role}
                        onClick={() => handleSwitchRoleClick(availableRole.role)}
                        disabled={switchingRole}
                        className={`flex items-center gap-2 px-4 py-2 ${colorClasses[availableRole.role]} border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm`}
                      >
                        <ArrowRightLeft className="h-4 w-4 flex-shrink-0" />
                        <span className="whitespace-nowrap">{switchingRole ? 'Switching...' : `Switch to ${availableRole.label.replace(' Dashboard', '')}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <UserCog className="h-4 w-4 flex-shrink-0" />
                <span>{user?.first_name} {user?.last_name}</span>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-colors text-sm"
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                <span className="whitespace-nowrap">Logout</span>
              </button>
            </div>

            {/* Mobile Menu Button - Visible on mobile and tablet */}
            <div className="lg:hidden flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-300">
                <UserCog className="h-4 w-4 flex-shrink-0" />
                <span className="truncate max-w-[80px]">{user?.first_name}</span>
              </div>
              <button
                ref={mobileMenuButtonRef}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2.5 bg-white/20 hover:bg-white/30 border-2 border-white/30 rounded-lg transition-colors flex items-center justify-center shadow-lg"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6 text-white" />
                ) : (
                  <Menu className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu - Portal */}
        {isMobileMenuOpen && mobileMenuPosition && typeof document !== 'undefined' && createPortal(
          <div 
            ref={mobileMenuRef}
            className="lg:hidden fixed bg-black/95 backdrop-blur-sm border border-white/20 rounded-lg shadow-xl z-[10001]"
            style={{
              top: `${mobileMenuPosition.top}px`,
              left: `${mobileMenuPosition.left}px`,
              width: `${mobileMenuPosition.width}px`,
            }}
          >
            <div className="px-3 py-4 space-y-2">
              {/* User Info */}
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-white border-b border-white/10 mb-2">
                <UserCog className="h-4 w-4 text-white" />
                <span className="text-white">{user?.first_name} {user?.last_name}</span>
              </div>

              {/* Role Switching Buttons */}
              {availableRoles.length > 0 && (
                <>
                  {availableRoles.map((availableRole) => {
                    const colorClasses = {
                      admin: 'bg-red-500/20 hover:bg-red-500/30 border-red-500/30',
                      host: 'bg-purple-500/20 hover:bg-purple-500/30 border-purple-500/30',
                      guest: 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/30',
                      usher: 'bg-green-500/20 hover:bg-green-500/30 border-green-500/30',
                    };
                    return (
                      <button
                        key={availableRole.role}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setMobileMenuPosition(null);
                          handleSwitchRoleClick(availableRole.role);
                        }}
                        disabled={switchingRole}
                        className={`w-full flex items-center gap-2 px-4 py-3 ${colorClasses[availableRole.role]} border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm text-white`}
                      >
                        <ArrowRightLeft className="h-4 w-4 flex-shrink-0 text-white" />
                        <span className="text-white">{switchingRole ? 'Switching...' : `Switch to ${availableRole.label.replace(' Dashboard', '')}`}</span>
                      </button>
                    );
                  })}
                </>
              )}
              
              {/* Logout Button */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setMobileMenuPosition(null);
                  handleLogout();
                }}
                className="w-full flex items-center gap-2 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-colors text-sm text-white"
              >
                <LogOut className="h-4 w-4 flex-shrink-0 text-white" />
                <span className="text-white">Logout</span>
              </button>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">Welcome, {user?.first_name}!</h1>
          <p className="text-sm sm:text-base text-gray-400">Manage guest attendance for your assigned events</p>
        </div>

        {/* Stats Cards — aligned with dashboard style */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8">
          <EnhancedLightCard
            title="Total Events"
            value={events.length}
            icon={<Calendar />}
            compactHorizontal
          />
          <EnhancedLightCard
            title="Total Guests"
            value={events.reduce((sum, event) => sum + event.guest_count, 0)}
            icon={<Users />}
            compactHorizontal
          />
          <EnhancedLightCard
            title="Attended"
            value={events.reduce((sum, event) => sum + event.attended_count, 0)}
            icon={<CheckCircle />}
            compactHorizontal
          />
        </div>

        {/* Events List */}
        <div className="space-y-4 sm:space-y-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Your Assigned Events</h2>
          
          {events.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Calendar className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-300 mb-2">No Events Assigned</h3>
              <p className="text-sm sm:text-base text-gray-400">You haven't been assigned to any events yet.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:gap-6">
              {events.map((event) => (
                <div
                  key={event.id}
                  onClick={() => handleEventClick(event.id)}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-purple-400 transition-colors truncate">
                          {event.title}
                        </h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusColor(event.status)}`}>
                          {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                        </span>
                      </div>
                      
                      <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 line-clamp-2">{event.description}</p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div className="flex items-center space-x-2 text-gray-300">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                          <span className="truncate">{formatDate(event.event_date)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-gray-300">
                          <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                          <span>{formatTime(event.event_time)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-gray-300 sm:col-span-2 md:col-span-1">
                          <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                          <span>{event.guest_count} guests</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-400 flex flex-wrap gap-1 sm:gap-2">
                        <span>Hosted by {event.host_name}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="truncate">{event.location}</span>
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0 sm:ml-4 sm:text-right">
                      <div className="text-xl sm:text-2xl font-bold text-white mb-1">
                        {event.attended_count}/{event.guest_count}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4">Attended</div>
                      
                      <div className="w-full sm:w-24 bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${event.guest_count > 0 ? (event.attended_count / event.guest_count) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Role Switch Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRoleSwitchConfirm}
        onClose={() => {
          setShowRoleSwitchConfirm(false);
          setTargetRole(null);
        }}
        onConfirm={handleSwitchRole}
        title={targetRole ? `Switch to ${availableRoles.find(r => r.role === targetRole)?.label || targetRole.charAt(0).toUpperCase() + targetRole.slice(1) + ' Dashboard'}` : 'Switch Dashboard'}
        message={
          targetRole === 'admin'
            ? 'Are you sure you want to switch to the admin dashboard? You will be able to manage the system and approve events.'
            : targetRole === 'guest'
            ? 'Are you sure you want to switch to the guest dashboard? You will be able to view and manage your guest invitations.'
            : targetRole === 'host'
            ? 'Are you sure you want to switch to the host dashboard? You will be able to create and manage your events.'
            : 'Are you sure you want to switch dashboards?'
        }
        confirmText="Switch"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={switchingRole}
      />
    </div>
  );
};

export default UsherDashboardPage;
